import {Module} from 'vuex';
import {
  ConversationSummary,
  MessageContainer, UiMessage
} from "@/components/communications/CommunicationInterfaces";
import {
  conversationClosed, conversationOptedOut,
  conversationRead,
  getConversationSummaries
} from "@/components/communications/services/CommunicationService";
import {ActionPayload, ReadActionPayload} from "@/components/communications/cards/actions/Actions";
import {ViewStateType} from "@/enums/ViewStateType";

export interface ContainerState {
  containerId: MessageContainer;
  // Persisted in the user's order
  messageIds: string[];
}

interface State {
  leftNavRefreshCounter: number;
  containerState: ContainerState[];
  // Track Inbox Messages and Organization Container Messages separately because there will be little overlap
  containerMessages: ConversationSummary[];
  inboxMessages: ConversationSummary[];
}

const state: State = {
  leftNavRefreshCounter: 0,
  containerState: [],
  containerMessages: [],
  inboxMessages: []
};

export default {
  namespaced: true,
  state,
  getters: {
    getMessageIdsForContainers(state): Set<string> {
      return state.containerState.reduce((accumulator: Set<string>, containerData: ContainerState) => {
        containerData.messageIds.forEach(id => accumulator.add(id));
        return accumulator;
      }, new Set<string>());
    },
    getMessagesForContainer: (state) => (containerId: MessageContainer) => {
      const containerData = state.containerState.find(c => c.containerId === containerId) || { messageIds: [] };
      const toReturn: ConversationSummary[] = [];
      // Need to loop messageIds and push to a new array to preserve the user's order.
      // Hopefully each column won't have "too many" messages so this will continue to perform okay - this is O(n^2)
      for (const messageId of containerData.messageIds) {
        const found = state.containerMessages.find(message => message.id === messageId);
        if (found) {
          toReturn.push(found);
        }
      }
      return toReturn;
    }
  },
  actions: {
    async loadContainerMessages({commit, getters}, payload: {messageType: number, contactType: number}) {
      const messageIds = getters.getMessageIdsForContainers as Set<string>;
      let data: ConversationSummary[] = [];
      if (messageIds.size > 0) {
        const parameters = {
          messageIds,
          messageType: payload.messageType,
          contactType: payload.contactType
        };
        data = await getConversationSummaries(parameters);
      }
      commit('setContainerMessages', data);
    },
    async loadInboxMessages({ commit }, payload: any) {
      const data = await getConversationSummaries(payload);
      commit('setInboxMessages', data);
    },
    async messageRead({ commit }, payload: ReadActionPayload) {
      await conversationRead(payload);
      commit('messageRead', payload);
      commit('refreshUnreadCount');
    },
    async messageClosed({ commit }, payload: ActionPayload) {
      await conversationClosed(payload);
      commit('messageRemoved', payload.messageIds);
      commit('refreshUnreadCount');
    },
    async messageOptedOut({ commit, rootState }, payload: ActionPayload) {
      const communicationType = rootState.viewState[ViewStateType.COMMUNICATION_SELECTED_MESSAGE_TYPE];
      const receiverType = rootState.viewState[ViewStateType.COMMUNICATION_SELECTED_RECEIVER];
      const optOutPayload = {
        messageIds: payload.messageIds,
        communicationTypeId: communicationType.id,
        receiverTypeId: receiverType.id
      };
      await conversationOptedOut(optOutPayload);
      commit('messageRemoved', payload.messageIds);
      commit('refreshUnreadCount');
    },
  },
  mutations: {
    setContainerMessages(state: State, data: ConversationSummary[]) {
      state.containerMessages = data;
    },
    setInboxMessages(state: State, data: ConversationSummary[]) {
      state.inboxMessages = data;
    },
    refreshUnreadCount(state: State) {
      state.leftNavRefreshCounter += 1;
    },
    removeMessageFromContainer(state: State, payload: { containerId: MessageContainer, removedIndex: number }) {
      const containerData = getContainerData(state, payload.containerId);
      containerData.messageIds.splice(payload.removedIndex, 1);
    },
    addMessageToContainer(state: State, payload: { containerId: MessageContainer, addedIndex: number, message: UiMessage }) {
      const containerData = getContainerData(state, payload.containerId);
      if (!state.containerMessages.some(message => message.id === payload.message.id)) {
        state.containerMessages.push({
          id: payload.message.id,
          messageContent: payload.message.messagePreview,
          isRead: payload.message.isRead,
          occurredOn: payload.message.lastActiveTime,
          contactFirstName: payload.message.targetFirstName,
          contactLastName: payload.message.targetLastName,
          campaignTitle: payload.message.latestCampaign,
          providerId: payload.message.providerId,
          facilityContactId: payload.message.facilityContactId,
          deliveryStatus: payload.message.deliveryStatus,
          providerTitle: payload.message.providerTitle
        });
      }
      containerData.messageIds.splice(payload.addedIndex, 0, payload.message.id);
    },
    messageRead(state: State, payload: ReadActionPayload) {
      state.containerMessages = readAll(state.containerMessages, payload);
      state.inboxMessages = readAll(state.inboxMessages, payload);
    },
    messageRemoved(state: State, messageIds: string[]) {
      state.containerMessages = state.containerMessages.filter(message => !messageIds.includes(message.id));
      state.inboxMessages = state.inboxMessages.filter(message => !messageIds.includes(message.id));
    }
  }
} as Module<State, any>;

// This can only be called from mutations because it modifies state
function getContainerData(state: State, containerId: MessageContainer): ContainerState {
  let containerData = state.containerState.find(c => c.containerId === containerId);
  if (!containerData) {
    containerData = { containerId: containerId, messageIds: [] };
    state.containerState.push(containerData);
  }
  return containerData;
}

/**
 * This returns what was passed in to allow an easy way to set the array to force Vue to notice the update of a property on an object in an array.
 * Also does .map(m=>m) to duplicate into a new array
 * Otherwise the change does not get detected due to Vue 2.x limitations with watching arrays.
 */
function readAll(messages: ConversationSummary[], payload: ReadActionPayload): ConversationSummary[] {
  messages
    .filter((message: ConversationSummary) => payload.messageIds.includes(message.id))
    .forEach((message: ConversationSummary) => message.isRead = payload.isRead);
  return messages.map(m => m);
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/store/modules/communication.ts